import ROUTES from '@/shared/constants/routes';
import type { ServerFileTarget } from '@/shared/download';
import { periodOf, splitPeriod } from './periods';
import { isDeclarableRegime, type DeclarableRegime } from './regimes';

export const REGIME_PARAM = 'regimen';

export const LAYOUT_PARAM = 'formato';

export const GROUP_PARAM = 'grupo';

export const ANNUAL_STAGE_PARAM = 'vista';

export const CATCH_UP_PATH = ROUTES.DECLARATIONS_CATCH_UP;

const MONTH_FIRST = /^(\d{1,2})-(\d{4})$/;

const YEAR_FIRST = /^(\d{4})-(\d{1,2})$/;

const YEAR_ONLY = /^\d{4}$/;

export const periodFromSlug = (slug?: string): string | null => {
	const monthFirst = slug?.match(MONTH_FIRST);
	if (monthFirst) return periodOf(Number(monthFirst[2]), Number(monthFirst[1]));
	const yearFirst = slug?.match(YEAR_FIRST);
	if (yearFirst) return periodOf(Number(yearFirst[1]), Number(yearFirst[2]));
	return null;
};

export const yearFromSlug = (slug?: string): number | null => (slug && YEAR_ONLY.test(slug) ? Number(slug) : null);

const ANNUAL_SLUG = /^anual-(\d{4})$/;

export const annualFromSlug = (slug?: string): number | null => {
	const match = slug?.match(ANNUAL_SLUG);
	return match ? Number(match[1]) : null;
};

export const annualPath = (year: number) => `${ROUTES.DECLARATIONS}/anual-${year}`;

const PAYROLL_SLUG = /^nomina-(\d{4}-(0[1-9]|1[0-2]))$/;

export const payrollFromSlug = (slug?: string): string | null => slug?.match(PAYROLL_SLUG)?.[1] ?? null;

export const payrollPath = (period: string) => `${ROUTES.DECLARATIONS}/nomina-${period}`;

export const RECEIPT_PARAM = 'recibo';

export const payrollReceiptPath = (period: string, receiptId: string) => withParams(payrollPath(period), { [RECEIPT_PARAM]: receiptId });

export const regimeFromParam = (value: string | null): DeclarableRegime | undefined =>
	value && isDeclarableRegime(value) ? value : undefined;

const periodSlug = (period: string) => {
	const { year, month } = splitPeriod(period);
	return `${month}-${year}`;
};

const withParams = (path: string, params: Record<string, string | undefined>) => {
	const search = new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]))).toString();
	return search ? `${path}?${search}` : path;
};

const withRegime = (path: string, regime?: DeclarableRegime) => withParams(path, { [REGIME_PARAM]: regime });

export const yearPath = (year: number) => `${ROUTES.DECLARATIONS}/${year}`;

export const periodPath = (period: string, regime?: DeclarableRegime) => withRegime(`${ROUTES.DECLARATIONS}/${periodSlug(period)}`, regime);

export const invoicesPath = (period: string, regime?: DeclarableRegime, group?: string) =>
	withParams(`${ROUTES.DECLARATIONS}/${periodSlug(period)}/facturas`, { [REGIME_PARAM]: regime, [GROUP_PARAM]: group });

export const invoiceFilePath = (uuid: string, format: 'pdf' | 'xml') => `declarations/new/me/invoices/${uuid}/${format}`;

export const invoiceFileTarget = (uuid: string, format: 'pdf' | 'xml'): ServerFileTarget => ({
	service: 'fiscal',
	kind: 'declaration_invoice',
	ref: { uuid, format },
});

const INVOICE_FILE_PATH = /^\/?declarations\/new\/me\/invoices\/([^/]+)\/(pdf|xml)$/;
const OPERATION_DOCUMENT_PATH = /^\/?declarations\/new\/me\/documents\/([^/]+)\/([^/?#]+)$/;

/** What the server needs to hand out the file behind a viewed path again; an external URL has none. */
export const documentFileTarget = (path: string): ServerFileTarget | undefined => {
	const invoice = INVOICE_FILE_PATH.exec(path);
	if (invoice) return invoiceFileTarget(invoice[1], invoice[2] as 'pdf' | 'xml');
	const document = OPERATION_DOCUMENT_PATH.exec(path);
	if (document) return { service: 'fiscal', kind: 'declaration_document', ref: { operation: document[1], document: document[2] } };
	return undefined;
};

export const invoicePath = (period: string, movementId: string, regime?: DeclarableRegime, group?: string) =>
	withParams(`${ROUTES.DECLARATIONS}/${periodSlug(period)}/facturas/${movementId}`, { [REGIME_PARAM]: regime, [GROUP_PARAM]: group });
